<template>
  <VRow class="field-row">
    <div class="field-row__fields">
      <div class="field-select-block">
        <VSelect
          v-model="field.type"
          :items="fieldTypes"
          class="field-select-type"
          item-text="label"
          item-value="type"
          :error-messages="fieldTypeErrorMessages"
          :label="$t('type')"
          outlined
          dense
          @change="handleTypeChange"
        />
      </div>
      <div class="block-select">
        <div class="field-label-block">
          <VTextField
            v-model="field.label"
            class="field-label"
            :label="$t('nameField')"
            :error-messages="fieldLabelErrorMessages"
            outlined
            dense
          />
        </div>

        <div v-if="fieldTypeSelect" class="block-available-values">
          <VMenu
            v-model="menuOpen"
            :close-on-content-click="false"
            max-height="400"
            offset-y
            attach
          >
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                class="select-available-values-wrapper"
              >
                <VTextField
                  :value="getValuesText()"
                  :label="$t('availableValues')"
                  :error-messages="fieldAvailableValuesErrorMessages"
                  outlined
                  dense
                  readonly
                  append-icon="mdi-chevron-down"
                  class="select-available-values"
                />
              </div>
            </template>
            <VCard>
              <VList max-height="400" style="overflow-y: auto;">
                <VListItem v-if="!showInput" @click="handleAddValueClick">
                  <VListItemIcon>
                    <VIcon>mdi-plus</VIcon>
                  </VListItemIcon>
                  <VListItemTitle>{{ $t('addValue') }}</VListItemTitle>
                </VListItem>

                <div
                  v-if="showInput"
                  class="add-value-container"
                  @click.stop
                >
                  <VTextField
                    ref="newValueInputRef"
                    v-model="field.newValue"
                    dense
                    outlined
                    :error-messages="duplicateValueError"
                    :error="hasDuplicateValue"
                    :placeholder="$t('name')"
                    class="add-value-input"
                    @keyup.enter.stop.prevent="addAvailableValue"
                    @input="checkDuplicateValue"
                    @click.stop
                  />
                  <VBtn
                    icon
                    small
                    @click.stop.prevent="addAvailableValue"
                    :disabled="!canAddValue"
                    class="add-value-btn"
                    :class="{ 'add-value-btn--disabled': !canAddValue }"
                  >
                    <img
                      v-if="canAddValue"
                      :src="require('@/assets/img/account/settings/check-fill-green.svg')"
                      alt="add"
                    />
                    <img
                      v-else
                      :src="require('@/assets/img/account/settings/check-fill-green.svg')"
                      class="add-value-btn__icon--disabled"
                      alt="add"
                    />
                  </VBtn>
                </div>

                <Draggable
                  :value="field.availableValues"
                  @input="handleDraggableInput"
                  :key="`draggable-${selectKey}-${(field.availableValues && field.availableValues.length) || 0}`"
                  class="values-list"
                >
                  <VListItem
                    v-for="(item, itemIndex) in field.availableValues"
                    :key="`${item}-${itemIndex}-${(field.availableValues && field.availableValues.length) || 0}`"
                    class="value-item"
                  >
                    <VListItemIcon class="drag-handle">
                      <VIcon>mdi-drag</VIcon>
                    </VListItemIcon>
                    <VListItemContent>
                      <VListItemTitle>{{ item }}</VListItemTitle>
                    </VListItemContent>
                    <VListItemAction>
                      <VBtn icon @click.stop.prevent="removeAvailableValue(item)" class="remove-btn">
                        <VIcon>mdi-close</VIcon>
                      </VBtn>
                    </VListItemAction>
                  </VListItem>
                </Draggable>
              </VList>
            </VCard>
          </VMenu>
        </div>
      </div>
      <div class="block-adds">
        <VCheckbox
          v-model="field.required"
          :label="$t('required')"
          class="field-checkbox-required"
        />
        <VBtn icon @click="$emit('removeField', index)">
          <VIcon>mdi-close</VIcon>
        </VBtn>
      </div>
    </div>
    <div class="field-row__text">
      {{ fieldText }}
    </div>
  </VRow>
</template>

<script>
import {
  ref, toRefs, computed, watch, onMounted, nextTick, getCurrentInstance,
} from '@vue/composition-api';
import { useI18n } from 'vue-i18n-composable';
import { addFieldTypes } from '@/enums';
import Draggable from 'vuedraggable';

export default {
  name: 'AccountSettingsInterviewFormRow',
  components: {
    Draggable,
  },
  props: {
    field: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    showValidationMessagesGlobal: {
      type: Boolean,
      required: true,
    },
    allFields: {
      type: Array,
      required: true,
    },
  },
  setup(props) {
    const { field, showValidationMessagesGlobal, allFields } = toRefs(props);
    const { t } = useI18n();

    const showValidationMessages = ref(false);

    const fieldTypes = computed(() => [
      { label: t('text'), type: addFieldTypes.STRING, text: t('textSubtitle') },
      { label: t('integer'), type: addFieldTypes.INTEGER, text: t('integerSubtitle') },
      { label: t('select'), type: addFieldTypes.SELECT, text: t('selectSubtitle') },
      { label: t('date'), type: addFieldTypes.DATE, text: t('dateSubtitle') },
    ]);

    const fieldText = computed(() => {
      const typeObj = fieldTypes.value.find((item) => item.type === field.value.type);
      return typeObj ? typeObj.text : '';
    });

    const fieldTypeSelect = computed(() => field.value.type === addFieldTypes.SELECT);

    const isFieldLabelRequired = computed(() => {
      return !field.value.label.length;
    });

    const isFieldTypeRequired = computed(() => {
      return !field.value.type;
    });

    const isFieldAvailableValuesRequired = computed(() => {
      return !field.value.availableValues?.length && fieldTypeSelect.value;
    });

    const isDuplicateLabel = computed(() => {
      const labelCount = allFields.value.filter((f) => f.label === field.value.label && f.label).length;
      return labelCount > 1;
    });

    const fieldTypeErrorMessages = computed(() => {
      const errors = [];

      if (isFieldTypeRequired.value && showValidationMessages.value) {
        errors.push(t('error-type-required'));
      }

      return errors;
    });

    const fieldLabelErrorMessages = computed(() => {
      const errors = [];

      if (isFieldLabelRequired.value && showValidationMessages.value) {
        errors.push(t('error-label-required'));
      }

      if (isDuplicateLabel.value && field.value.label && showValidationMessages.value) {
        errors.push(t('error-duplicate-label'));
      }

      return errors;
    });

    const fieldAvailableValuesErrorMessages = computed(() => {
      const errors = [];

      if (isFieldAvailableValuesRequired.value && showValidationMessages.value) {
        errors.push(t('error-available-values-required'));
      }

      return errors;
    });

    const isValid = computed(() => !(isFieldLabelRequired.value || isFieldTypeRequired.value
      || isFieldAvailableValuesRequired.value || isDuplicateLabel.value));

    const showInput = ref(false);
    const selectKey = ref(0);
    const preservedAvailableValues = ref([]);
    const menuOpen = ref(false);
    const duplicateValueError = ref('');
    const hasDuplicateValue = ref(false);
    const newValueInputRef = ref(null);

    const canAddValue = computed(() => {
      const value = field.value.newValue?.trim();
      return value && !hasDuplicateValue.value;
    });

    const ensureAvailableValuesReactive = () => {
      if (fieldTypeSelect.value) {
        if (!field.value.availableValues || !Array.isArray(field.value.availableValues)) {
          const instance = getCurrentInstance();
          if (instance && instance.proxy && instance.proxy.$set) {
            instance.proxy.$set(field.value, 'availableValues', []);
          } else {
            field.value.availableValues = [];
          }
        }
      }
    };

    const handleDraggableInput = async (newValues) => {
      const instance = getCurrentInstance();
      if (instance && instance.proxy && instance.proxy.$set) {
        instance.proxy.$set(field.value, 'availableValues', newValues);
        const fieldIndex = props.index;
        instance.proxy.$set(allFields.value, fieldIndex, { ...field.value, availableValues: newValues });
      } else {
        field.value.availableValues = newValues;
      }
      await nextTick();
      selectKey.value += 1;
    };

    const handleTypeChange = (newType) => {
      if (newType === 'select') {
        if (!field.value.availableValues || !Array.isArray(field.value.availableValues)) {
          field.value.availableValues = preservedAvailableValues.value && Array.isArray(preservedAvailableValues.value) ? [...preservedAvailableValues.value] : [];
        } else {
          field.value.availableValues = preservedAvailableValues.value && Array.isArray(preservedAvailableValues.value) ? [...preservedAvailableValues.value] : field.value.availableValues;
        }
      } else {
        if (field.value.availableValues && Array.isArray(field.value.availableValues)) {
          preservedAvailableValues.value = [...field.value.availableValues];
        } else {
          preservedAvailableValues.value = [];
        }
        field.value.availableValues = [];
      }
      ensureAvailableValuesReactive();
    };

    const checkDuplicateValue = () => {
      const value = field.value.newValue?.trim();
      if (!value) {
        duplicateValueError.value = '';
        hasDuplicateValue.value = false;
        return;
      }

      ensureAvailableValuesReactive();
      if (!field.value.availableValues || !Array.isArray(field.value.availableValues)) {
        return;
      }

      if (!fieldTypeSelect.value) {
        return;
      }

      const isDuplicate = field.value.availableValues.some(
        (existingValue) => existingValue.trim().toLowerCase() === value.toLowerCase(),
      );

      if (isDuplicate) {
        duplicateValueError.value = t('error-duplicate-value');
        hasDuplicateValue.value = true;
      } else {
        duplicateValueError.value = '';
        hasDuplicateValue.value = false;
      }
    };

    const handleAddValueClick = async () => {
      showInput.value = true;
      await nextTick();
      if (newValueInputRef.value && newValueInputRef.value.$el) {
        const input = newValueInputRef.value.$el.querySelector('input');
        if (input) {
          input.focus();
        }
      }
    };

    const addAvailableValue = async (event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      const value = field.value.newValue?.trim();
      if (!value) return;

      ensureAvailableValuesReactive();
      if (!Array.isArray(field.value.availableValues)) {
        field.value.availableValues = [];
      }

      const isDuplicate = field.value.availableValues.some(
        (existingValue) => existingValue.trim().toLowerCase() === value.toLowerCase(),
      );

      if (isDuplicate) {
        duplicateValueError.value = t('error-duplicate-value');
        hasDuplicateValue.value = true;
        return;
      }

      field.value.availableValues = [...field.value.availableValues, value];
      field.value.newValue = '';
      duplicateValueError.value = '';
      hasDuplicateValue.value = false;

      await nextTick();
      if (newValueInputRef.value && newValueInputRef.value.$el) {
        const input = newValueInputRef.value.$el.querySelector('input');
        if (input) {
          input.focus();
        }
      }
    };

    const removeAvailableValue = async (value) => {
      ensureAvailableValuesReactive();
      if (!field.value.availableValues || !Array.isArray(field.value.availableValues)) {
        const instance = getCurrentInstance();
        if (instance && instance.proxy && instance.proxy.$set) {
          instance.proxy.$set(field.value, 'availableValues', []);
        } else {
          field.value.availableValues = [];
        }
        return;
      }
      const index = field.value.availableValues.indexOf(value);
      if (index > -1) {
        const newArray = field.value.availableValues.filter((v) => v !== value);
        const instance = getCurrentInstance();
        if (instance && instance.proxy && instance.proxy.$set) {
          const fieldIndex = props.index;
          instance.proxy.$set(field.value, 'availableValues', newArray);
          instance.proxy.$set(allFields.value, fieldIndex, { ...field.value, availableValues: newArray });
        } else {
          field.value.availableValues = newArray;
        }
        await nextTick();
        selectKey.value += 1;
      }
      checkDuplicateValue();
    };

    const getValuesText = () => {
      const count = field.value.availableValues?.length || 0;
      if (count === 0) {
        return '';
      }
      const lastDigit = count % 10;
      const lastTwoDigits = count % 100;
      let word;
      if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
        word = t('valuesGenitive');
      } else if (lastDigit === 1) {
        word = t('valuesNominative');
      } else if (lastDigit >= 2 && lastDigit <= 4) {
        word = t('valuesAccusative');
      } else {
        word = t('valuesGenitive');
      }
      return `${count} ${word}`;
    };

    const validateField = () => {
      field.value.isValid = isValid.value;
    };

    watch(showValidationMessagesGlobal, (flag) => {
      if (!flag) {
        return;
      }

      showValidationMessages.value = true;
    });

    watch(field, () => {
      validateField();
      ensureAvailableValuesReactive();
    }, { deep: true });

    onMounted(() => {
      validateField();
      ensureAvailableValuesReactive();
    });

    return {
      fieldTypes,
      fieldText,
      showInput,
      addAvailableValue,
      removeAvailableValue,
      selectKey,
      validateField,
      isFieldLabelRequired,
      isFieldTypeRequired,
      isFieldAvailableValuesRequired,
      fieldLabelErrorMessages,
      fieldAvailableValuesErrorMessages,
      fieldTypeErrorMessages,
      handleTypeChange,
      fieldTypeSelect,
      duplicateValueError,
      hasDuplicateValue,
      checkDuplicateValue,
      canAddValue,
      menuOpen,
      getValuesText,
      handleAddValueClick,
      ensureAvailableValuesReactive,
      handleDraggableInput,
    };
  },
};
</script>

<style lang="scss" scoped>
.field-row {
  margin: 20px 20px 20px 0px;
  display: flex;
  flex-direction: column;
  width: 100%;
  @media screen and (max-width: 768px) {
    margin: 0 0 32px 0;
  }
  &__fields {
    display: flex;
    @media screen and (max-width: 768px) {
      flex-direction: column;
    }
  }
  &__text {
    color: #808080;
    font-size: 14px;
    line-height: 120%;
    font-weight: 300;
  }
}

.field-select-type {
  max-width: 210px;
  min-width: 210px;
  margin-bottom: 6px;
  @media screen and (max-width: 768px) {
    max-width: 100%;
    min-width: 100%;
  }
  &::v-deep {
    .v-input__slot{
      background-color: #F3F5F7;
      font-size: 15px;
      border-radius: 4px !important;
      fieldset {
        border: none;
      }
    }
  }
}

.add-value-container {
  display: flex;
  align-items: flex-start;
  padding: 12px 20px;
}
.add-value-input {
  flex: 1;
  margin-right: 8px;
}
.add-value-btn {
  min-width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 5px;

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &__icon--disabled {
    filter: grayscale(100%) brightness(0.7);
  }

  img {
    display: block;
  }
}

.field-label-block {
  margin: 0 20px;;
  @media screen and (max-width: 768px) {
    margin: 12px 0 0 0;
  }
}

.field-label {
  margin-bottom: 2px;
}

.field-checkbox-required {
  margin: 0 20px 0 0;
  height: 50%;
}

.block-select {
  display: flex;
  flex-direction: column;
  width: 40%;
  @media screen and (max-width: 1024px) {
    width: 100%;
  }
}

.block-adds {
  display: flex;
  min-width: 210px;
  @media screen and (max-width: 768px) {
    justify-content: space-between;
  }
}

  .value-item {
    cursor: grab;
    position: relative;
    transition: background-color 0.2s;

    &:hover {
      background-color: rgba(0, 0, 0, 0.04);

      .drag-handle {
        opacity: 1;
      }
    }

    &:active {
      cursor: grabbing;
    }

    .drag-handle {
      opacity: 0;
      cursor: grab;
      transition: opacity 0.2s;
      min-width: 24px;
      pointer-events: none;
      display: flex;
      align-items: center;
      justify-content: center;
      align-self: center !important;
      margin: 0 12px 0 0 !important;

      .v-icon {
        color: rgba(0, 0, 0, 0.54);
      }
    }

    .remove-btn {
      pointer-events: auto;
      cursor: pointer;
    }

    &[draggable="true"] {
      cursor: grabbing;
    }
  }

  ::v-deep .sortable-ghost {
    opacity: 0.4;
  }

  ::v-deep .sortable-drag {
    background-color: rgba(0, 0, 0, 0.04) !important;

    .drag-handle {
      opacity: 1;
    }
  }

.block-available-values {
  margin: 0 20px 20px 20px;
  display: flex;
  flex-direction: column;
  @media screen and (max-width: 768px) {
    margin: 0 0 12px 0;
  }
}

.select-available-values-wrapper {
  width: 100%;
  cursor: pointer;
  margin-bottom: 6px;
}

.select-available-values {
  pointer-events: none;

  ::v-deep .v-input__slot {
    cursor: pointer;
  }

  ::v-deep .v-input__control {
    cursor: pointer;
  }

  ::v-deep .v-input__append-inner {
    cursor: pointer;
  }

  ::v-deep .v-input__prepend-inner {
    cursor: pointer;
  }

  ::v-deep input {
    cursor: pointer;
  }
}

.values-list {
  display: flex;
  flex-direction: column;
}

.add-value-btn {
  cursor: pointer;
}
::v-deep {
  .v-list-item {
    min-height: 35px !important;
    max-height: 35px !important;
    padding: 0 20px !important;
  }
  .v-list-item__title {
    color: #202020;
    font-size: 15px !important;
    line-height: 15px !important;
  }

  .v-list-item--link {
    text-align: center;
    justify-content: center;
    font-size: 14px;
    color: #202020;
    min-height: 50px !important;
    max-height: 50px !important;
    .v-list-item__icon {
      margin-right: 4px !important;
      margin-top: 12px !important;
      margin-bottom: 0 !important;
    }
    .v-list-item__title {
      flex: none;
    }
  }
}
</style>

<i18n locale="en" lang="yaml">
  nameField: Specify the field name
  name: Specify the value
  availableValues: Available values
  valuesNominative: value
  valuesAccusative: values
  valuesGenitive: values
  missingData: Missing data
  required: Required
  newValue: New value
  text: Text
  textSubtitle: 'Text – The user can enter any text at their discretion.'
  integer: Integer
  integerSubtitle: 'A number is an integer (example: 2025)'
  select: The selection field
  selectSubtitle: The selection field is a text field with already filled fields for the candidate's choice.
  date: Date
  dateSubtitle: 'Date format 12.02.2025'
  addValue: Add value
  type: Type
  error-label-required: Required field to fill in
  error-available-values-required: Add at least 1 value
  error-type-required: Select the type
  error-duplicate-label: Duplicate label found
  error-duplicate-value: This value already exists
</i18n>

<i18n locale="ru" lang="yaml">
  nameField: Укажите название поля
  name: Укажите название
  availableValues: Доступные значения
  valuesNominative: значение
  valuesAccusative: значения
  valuesGenitive: значений
  missingData: Отсутствуют данные
  required: Обязательное поле
  newValue: Новое значение
  text: Текст
  textSubtitle: 'Текст – Пользователь сможет внести любой текст на свое усмотрение'
  integer: Число
  integerSubtitle: 'Число – Целое число (пример: 2025)'
  select: Поле выбора
  selectSubtitle: Поле выбора – Текстовое поле с уже заполненными полями на выбор кандидата.
  date: Дата
  dateSubtitle: 'Дата - формат 12.02.2025'
  addValue: Добавить значение
  type: Тип
  error-label-required: Обязательное поле для заполнения
  error-available-values-required: Добавьте хотя бы 1 значение
  error-type-required: Выберите тип
  error-duplicate-label: Найдено дублирующееся название
  error-duplicate-value: Это значение уже существует
</i18n>
